import React, { useState, useCallback, useMemo, useRef } from "react";
import "./App.css";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Collapse,
} from "@mui/material";
import {
  AutoAwesome as SparkleIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  Work as WorkIcon,
  CheckCircle as CheckIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/build/pdf";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import ControlsBar from "./components/ControlsBar";
import SEOHead from "./components/SEOHead";

GlobalWorkerOptions.workerSrc = pdfjsWorker;

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

type Category   = "behavioral" | "technical" | "situational" | "culture";
type Difficulty = "easy" | "medium" | "hard";

interface Question {
  question:        string;
  category:        Category;
  difficulty:      Difficulty;
  suggestedAnswer: string;
  tips:            string;
}

type Filter = "all" | Category;

// ─── Inner app ───────────────────────────────────────────────

const InterviewPrepApp: React.FC = () => {
  const { t, isRTL, language } = useLanguage();
  const muiTheme = useMuiTheme();
  const isDark = muiTheme.palette.mode === "dark";

  const [jobDescription, setJobDescription] = useState("");
  const [resume,         setResume]         = useState("");
  const [resumeFileName, setResumeFileName] = useState("");
  const [loading,        setLoading]        = useState(false);
  const [pdfLoading,     setPdfLoading]     = useState(false);
  const [error,          setError]          = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  const extractTextFromPDF = async (file: File) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await getDocument({ data: arrayBuffer }).promise;
    let text = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const pageText = content.items.map((item: any) => item.str).join(" ");
      text += pageText + "\n";
    }
    return text;
  };

  const handleResumeFileUpload = useCallback(async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setResumeFileName(file.name);
    setError("");
    try {
      if (file.type === "application/pdf") {
        const text = await extractTextFromPDF(file);
        setResume(text);
      } else if (file.type === "text/plain" || file.name.endsWith(".txt")) {
        const text = await file.text();
        setResume(text);
      } else {
        throw new Error(t("errors.unsupportedFileType"));
      }
    } catch (err: any) {
      console.error("Error processing resume file:", err);
      setError(`${t("errors.errorProcessingFile")}: ${err.message}`);
      setResumeFileName("");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [t]);

  const [questions, setQuestions] = useState<Question[]>([]);
  const [expanded,  setExpanded]  = useState<Record<number, boolean>>({});
  const [filter,    setFilter]    = useState<Filter>("all");

  const visibleQuestions = useMemo(
    () => filter === "all" ? questions : questions.filter(q => q.category === filter),
    [questions, filter],
  );

  const handleSubmit = useCallback(async () => {
    setError("");
    if (jobDescription.trim().length < 30) {
      setError(t("errors.jdTooShort"));
      return;
    }
    setLoading(true);
    setQuestions([]);
    setExpanded({});
    setFilter("all");
    try {
      const res = await fetch(`${API_URL}/api/interview-questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobDescription, resume, language }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "request failed");
      }
      const data = await res.json();
      setQuestions(data.questions || []);
      setTimeout(() => {
        document.getElementById("results-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (err: any) {
      setError(err?.message?.includes("fetch") ? t("errors.network") : t("errors.failed"));
    } finally {
      setLoading(false);
    }
  }, [jobDescription, resume, language, t]);

  const handleDownloadPdf = useCallback(async () => {
    if (questions.length === 0) return;
    setPdfLoading(true);
    try {
      const firstLine = jobDescription.split("\n").find(l => l.trim()) || "";
      const jobTitle  = firstLine.length > 100 ? firstLine.slice(0, 100) : firstLine;
      const res = await fetch(`${API_URL}/api/generate-interview-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobTitle, questions, language }),
      });
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url;
      a.download = "interview-prep.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError(t("errors.pdfFailed"));
    } finally {
      setPdfLoading(false);
    }
  }, [questions, jobDescription, language, t]);

  const handleReset = useCallback(() => {
    setQuestions([]);
    setExpanded({});
    setError("");
    setFilter("all");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const toggleExpanded = (idx: number) => setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));

  const categoryStyle = (cat: Category) => {
    const styles = {
      behavioral:  { bg: isDark ? "rgba(255,148,72,0.18)"  : "rgba(255,220,172,0.85)", fg: isDark ? "#FFC799" : "#c2580c" },
      technical:   { bg: isDark ? "rgba(120,150,255,0.16)" : "rgba(220,232,255,0.95)", fg: isDark ? "#A9C0FF" : "#1d4ed8" },
      situational: { bg: isDark ? "rgba(100,200,140,0.14)" : "rgba(220,252,231,0.95)", fg: isDark ? "#9FE5BC" : "#166534" },
      culture:     { bg: isDark ? "rgba(220,140,200,0.14)" : "rgba(252,231,243,0.95)", fg: isDark ? "#F2B6DD" : "#9d174d" },
    };
    return styles[cat];
  };

  const difficultyStyle = (d: Difficulty) => {
    const styles = {
      easy:   { bg: isDark ? "rgba(100,200,140,0.14)" : "rgba(220,252,231,0.95)", fg: isDark ? "#9FE5BC" : "#166534" },
      medium: { bg: isDark ? "rgba(255,200,100,0.16)" : "rgba(254,243,199,0.95)", fg: isDark ? "#FFD995" : "#92400e" },
      hard:   { bg: isDark ? "rgba(255,120,120,0.16)" : "rgba(254,226,226,0.95)", fg: isDark ? "#FFB0B0" : "#991b1b" },
    };
    return styles[d];
  };

  return (
    <>
      <SEOHead />
      <ControlsBar />

      {/* Ambient orbs */}
      <div className="ambient-stage" aria-hidden="true">
        <div className="orb orb-one" />
        <div className="orb orb-two" />
        <div className="orb orb-three" />
      </div>

      <Container maxWidth="md" sx={{ pt: { xs: "5.75rem", md: "5rem" }, pb: { xs: 6, md: 8 }, position: "relative" }}>

        {/* Hero */}
        <Paper elevation={0} className="hero-panel" sx={{ mb: 4 }}>
          <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: "center" }}>
            <Chip
              icon={<SparkleIcon sx={{ fontSize: "1rem !important" }} />}
              label="AI Interview Prep"
              className="hero-feature-chip"
              sx={{ fontWeight: 800 }}
            />
          </Stack>
          <Typography variant="h1" className="hero-title">
            {t("app.headline")}
          </Typography>
          <Typography variant="h6" className="hero-subtitle">
            {t("app.subhead")}
          </Typography>
        </Paper>

        {/* Form */}
        <Paper elevation={0} sx={{
          p: { xs: 2.5, sm: 4 }, borderRadius: 4,
          background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.72)",
          border: "1px solid",
          borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.88)",
          backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
          boxShadow: isDark ? "0 18px 60px rgba(0,0,0,0.32)" : "0 18px 60px rgba(120,70,30,0.10)",
        }}>
          <Stack spacing={3}>
            {/* Job description */}
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: "center" }}>
                <WorkIcon sx={{ fontSize: 18, color: "#FF8428" }} />
                <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
                  {t("form.jdLabel")}
                </Typography>
              </Stack>
              <TextField
                multiline
                rows={6}
                fullWidth
                value={jobDescription}
                onChange={e => setJobDescription(e.target.value)}
                placeholder={t("form.jdPlaceholder")}
                helperText={t("form.jdHint")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    background: isDark ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.9)",
                  },
                }}
              />
            </Box>

            {/* Resume */}
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: "center" }}>
                <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
                  {t("form.resumeLabel")}
                </Typography>
                <Chip
                  label={t("form.resumeOptional")}
                  size="small"
                  sx={{
                    height: 20, fontSize: "0.68rem", fontWeight: 700,
                    background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                    color: "text.secondary",
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, alignItems: "center" }}>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.14)",
                    color: "text.primary",
                    "&:hover": {
                      borderColor: "#FF8428",
                      background: "rgba(255,132,40,0.08)",
                    },
                  }}
                >
                  {t("form.uploadResume")}
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".pdf,.txt,text/plain,application/pdf"
                    onChange={handleResumeFileUpload}
                  />
                </Button>
                {resumeFileName && (
                  <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", fontWeight: 600 }}>
                    {resumeFileName}
                  </Typography>
                )}
              </Stack>
              <TextField
                multiline
                rows={4}
                fullWidth
                value={resume}
                onChange={e => { setResume(e.target.value); setResumeFileName(""); }}
                placeholder={t("form.resumePlaceholder")}
                helperText={t("form.resumeHint")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    background: isDark ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.9)",
                  },
                }}
              />
            </Box>

            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
            )}

            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading || jobDescription.trim().length < 30}
              startIcon={loading ? <CircularProgress size={18} sx={{ color: "inherit" }} /> : <SparkleIcon />}
              sx={{
                borderRadius: 999, py: 1.5,
                background: "linear-gradient(135deg, #FF8428 0%, #D95F13 100%)",
                color: "#fff", fontWeight: 800, fontSize: "1rem",
                boxShadow: "0 12px 32px rgba(255,132,40,0.40)",
                "&:hover":        { background: "linear-gradient(135deg, #FFB06A 0%, #FF8428 100%)" },
                "&.Mui-disabled": {
                  background: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
                  color:      isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
                },
              }}
            >
              {loading ? t("form.submitting") : t("form.submit")}
            </Button>
          </Stack>
        </Paper>

        {/* Results */}
        {questions.length > 0 && (
          <Box id="results-anchor" sx={{ mt: 6 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{
                mb: 3,
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
              }}
            >
              <Box>
                <Typography sx={{ fontSize: { xs: "1.5rem", sm: "2rem" }, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1 }}>
                  {t("results.heading")}
                </Typography>
                <Typography sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.95rem", mt: 0.5 }}>
                  {t("results.subheading").replace("{{count}}", String(questions.length))}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  sx={{ borderRadius: 999, fontWeight: 700, textTransform: "none" }}
                >
                  {t("form.reset")}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={pdfLoading ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <PdfIcon />}
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading}
                  sx={{
                    borderRadius: 999, fontWeight: 700, textTransform: "none",
                    background: "linear-gradient(135deg, #FF8428 0%, #D95F13 100%)",
                    color: "#fff",
                    "&:hover": { background: "linear-gradient(135deg, #FFB06A 0%, #FF8428 100%)" },
                  }}
                >
                  {pdfLoading ? t("results.downloading") : t("results.downloadPdf")}
                </Button>
              </Stack>
            </Stack>

            {/* Category filter */}
            <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: "wrap", rowGap: 1 }}>
              {(["all", "behavioral", "technical", "situational", "culture"] as Filter[]).map(f => {
                const active = filter === f;
                const cnt = f === "all" ? questions.length : questions.filter(q => q.category === f).length;
                if (f !== "all" && cnt === 0) return null;
                return (
                  <Chip
                    key={f}
                    label={`${f === "all" ? t("filter.all") : t(`category.${f}`)} (${cnt})`}
                    onClick={() => setFilter(f)}
                    sx={{
                      fontWeight: 700, borderRadius: "999px !important",
                      background: active
                        ? (isDark ? "rgba(255,138,42,0.26)" : "rgba(255,220,172,0.85)")
                        : (isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"),
                      border: "1.5px solid",
                      borderColor: active ? "rgba(255,138,42,0.55)" : "transparent",
                      color: active ? "#FF8428" : "text.primary",
                      transition: "all 0.15s ease",
                      "&:hover": { opacity: 0.85 },
                    }}
                  />
                );
              })}
            </Stack>

            {/* Question cards */}
            <Stack spacing={2}>
              {visibleQuestions.map((q, displayIdx) => {
                const idx    = questions.indexOf(q);
                const isOpen = !!expanded[idx];
                const catSt  = categoryStyle(q.category);
                const diffSt = difficultyStyle(q.difficulty);
                return (
                  <Paper
                    key={idx}
                    elevation={0}
                    sx={{
                      p: { xs: 2.5, sm: 3 }, borderRadius: 3,
                      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.78)",
                      border: "1px solid",
                      borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.88)",
                      backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
                      boxShadow: isDark ? "0 8px 28px rgba(0,0,0,0.30)" : "0 8px 28px rgba(120,70,30,0.08)",
                      transition: "transform 0.18s ease, box-shadow 0.18s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: isDark ? "0 12px 36px rgba(0,0,0,0.36)" : "0 12px 36px rgba(120,70,30,0.13)",
                      },
                    }}
                  >
                    <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                        background: "linear-gradient(135deg, #FF8428 0%, #D95F13 100%)",
                        color: "#fff", fontWeight: 900, fontSize: "0.95rem",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: "0 6px 18px rgba(255,132,40,0.35)",
                      }}>
                        {displayIdx + 1}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap", rowGap: 0.5 }}>
                          <Chip
                            label={t(`category.${q.category}`)}
                            size="small"
                            sx={{
                              height: 22, fontSize: "0.68rem", fontWeight: 800,
                              background: catSt.bg, color: catSt.fg,
                              textTransform: isRTL ? "none" : "uppercase",
                              letterSpacing: isRTL ? "normal" : "0.04em",
                            }}
                          />
                          <Chip
                            label={t(`difficulty.${q.difficulty}`)}
                            size="small"
                            sx={{
                              height: 22, fontSize: "0.68rem", fontWeight: 800,
                              background: diffSt.bg, color: diffSt.fg,
                              textTransform: isRTL ? "none" : "uppercase",
                              letterSpacing: isRTL ? "normal" : "0.04em",
                            }}
                          />
                        </Stack>

                        <Typography sx={{
                          fontSize: { xs: "1.05rem", sm: "1.15rem" },
                          fontWeight: 700, lineHeight: 1.4,
                          color: "text.primary", mb: 1.5,
                        }}>
                          {q.question}
                        </Typography>

                        <Button
                          size="small"
                          onClick={() => toggleExpanded(idx)}
                          endIcon={
                            <ExpandIcon sx={{
                              transition: "transform 0.2s ease",
                              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                            }} />
                          }
                          sx={{
                            textTransform: "none", fontWeight: 700, fontSize: "0.82rem",
                            color: "#FF8428",
                            px: 1.5, borderRadius: 999,
                            "&:hover": { background: isDark ? "rgba(255,132,40,0.10)" : "rgba(255,132,40,0.08)" },
                          }}
                        >
                          {isOpen ? t("results.hideAnswer") : t("results.showAnswer")}
                        </Button>

                        <Collapse in={isOpen}>
                          <Box sx={{ mt: 2 }}>
                            <Typography sx={{
                              fontSize: "0.72rem", fontWeight: 800, color: "#c2580c",
                              textTransform: isRTL ? "none" : "uppercase",
                              letterSpacing: isRTL ? "normal" : "0.10em",
                              mb: 0.75,
                            }}>
                              {t("results.suggestedAnswer")}
                            </Typography>
                            <Typography sx={{ fontSize: "0.95rem", lineHeight: 1.65, color: "text.primary", mb: q.tips ? 2 : 0 }}>
                              {q.suggestedAnswer}
                            </Typography>
                            {q.tips && (
                              <Box sx={{
                                p: 1.5,
                                borderRadius: 2,
                                background: isDark ? "rgba(255,132,40,0.10)" : "rgba(255,245,230,0.95)",
                                [isRTL ? "borderRight" : "borderLeft"]: "3px solid #FF8428",
                                display: "flex", gap: 1, alignItems: "flex-start",
                              }}>
                                <CheckIcon sx={{ fontSize: 16, color: "#FF8428", flexShrink: 0, mt: 0.3 }} />
                                <Typography sx={{ fontSize: "0.85rem", lineHeight: 1.5, color: isDark ? "#FFD0A3" : "#4a3a2c" }}>
                                  <Box component="span" sx={{ fontWeight: 800, color: "#c2580c", mr: 0.5 }}>
                                    {t("results.tip")}:
                                  </Box>
                                  {q.tips}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        )}

      </Container>
    </>
  );
};

const App: React.FC = () => (
  <ThemeProvider>
    <LanguageProvider>
      <InterviewPrepApp />
    </LanguageProvider>
  </ThemeProvider>
);

export default App;
